import React, { useEffect, useRef, useState } from "react";
import { DataGrid, GridColDef, GridRowId } from "@mui/x-data-grid";
import { InventoryApiService } from "../../Service/InventoryApiService";
import DataGridLoader from '../Common/DataGridLoader';
import { Typography } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { DateRange } from "react-date-range";
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import { CommonService } from "../../Service/CommonService";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import Select from "react-select";
import { Accordion } from "react-bootstrap";

const TTBReport: React.FC = () => {
  const previousMonthRange = CommonService.getPreviousMonthDateRange();
  const [companiesList, setCompaniesList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [sortField, setSortField] = useState("companyName"); // Default sort
  const [sortDirection, setSortDirection] = useState("asc"); // Default ascending
  const controllerRef = useRef<AbortController | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const datePickerRef = useRef<HTMLDivElement | null>(null);
  const mainInputRef = useRef<HTMLDivElement | null>(null);
  const [mainPickerStyle, setMainPickerStyle] = useState<React.CSSProperties>({});
  const [isMainDatePickerOpen, setIsMainDatePickerOpen] = useState(false);
  const [filter, setFilter] = useState<any>({
      startDate: previousMonthRange.startDateText,
      endDate: previousMonthRange.endDateText
  });
  // final selected values
  const [selectedDateRange, setSelectedDateRange] = useState<any>({
      startDate: filter.startDate ?? null,
      endDate: filter.endDate ?? null,
      key: "selection",
  });
  // temp values for picker
  const [tempRange, setTempRange] = useState<any>({
      startDate: filter.startDate ?? null,
      endDate: filter.endDate ?? null,
      key: "selection",
  });
  const [dateRangeKey, setDateRangeKey] = useState(0);
  const options = [
    { value: "Previous Month", label: "Previous Month" },
    { value: "Last 3 Months", label: "Last 3 Months" },
    { value: "Last 6 Months", label: "Last 6 Months" },
    { value: "Last 12 Months", label: "Last 12 Months" },
    { value: "Custom", label: "Custom" },
  ];
  const [selectedPeriod, setSelectedPeriod] = useState(options[0].value);
  const isFilterApplied = Object.values(filter).some(
    (value) => value !== null && value !== "" && value !== undefined
  );
  const localeText = {
    noRowsLabel: isLoading ? "Fetching records ..." : "No Record Found",
  };

  const [dateRangeError, setDateRangeError] = useState<string | null>(null);

  const isDateRangeValid = (start: string | null, end: string | null): boolean => {
    if (!start || !end) return true;
    const s = new Date(start + "T00:00:00");
    const e = new Date(end + "T00:00:00");
    const months = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
    return months < 12;
  };
  const formatDate = (date: Date | null) => {
    if (!date) return null;

    const parsedDate =
        typeof date === "string"
            ? new Date(date + "T00:00:00")
            : date;

    return parsedDate.toLocaleDateString("en-CA");
  };

  useEffect(() => {
    getTTBReport(page, pageSize, filter, sortField, sortDirection);
  }, []);

  useEffect(() => {
    if (!isMainDatePickerOpen) return;
    const update = () =>
      setMainPickerStyle(getFixedPickerPosition(mainInputRef.current, 700));
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [isMainDatePickerOpen]);



  //add tooltip
  const CellWithTooltip = ({ value }: { value: string | number }) => {
    return (
      <Tooltip arrow title={value?.toString()}>
        <span>{value?.toString()}</span>
      </Tooltip>
    );
  };

  const areDateRangesEqual = (aStart: Date | null, aEnd: Date | null, bStart: Date | null, bEnd: Date | null) => {
    if (!aStart || !aEnd || !bStart || !bEnd) return false;
    return (
      aStart.getFullYear() === bStart.getFullYear() &&
      aStart.getMonth() === bStart.getMonth() &&
      aStart.getDate() === bStart.getDate() &&
      aEnd.getFullYear() === bEnd.getFullYear() &&
      aEnd.getMonth() === bEnd.getMonth() &&
      aEnd.getDate() === bEnd.getDate()
    );
  };

  const getRangeForPeriod = (value: string) => {
    const today = new Date();
    if (value === "Previous Month") {
      const range = CommonService.getPreviousMonthDateRange();
      return { start: range.startDate, end: range.endDate };
    }

    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    if (value === "Last 3 Months") {
      return {
        start: new Date(today.getFullYear(), today.getMonth() - 2, 1),
        end,
      };
    }

    if (value === "Last 6 Months") {
      return {
        start: new Date(today.getFullYear(), today.getMonth() - 5, 1),
        end,
      };
    }

    if (value === "Last 12 Months") {
      return {
        start: new Date(today.getFullYear() - 1, today.getMonth() + 1, 1),
        end,
      };
    }

    return { start: null, end: null };
  };

  const getMatchingPeriod = (start: Date | null, end: Date | null) => {
    if (!start || !end) return "Custom";

    const previousMonth = CommonService.getPreviousMonthDateRange();
    if (areDateRangesEqual(start, end, previousMonth.startDate, previousMonth.endDate)) {
      return "Previous Month";
    }

    const last3 = getRangeForPeriod("Last 3 Months");
    if (areDateRangesEqual(start, end, last3.start, last3.end)) {
      return "Last 3 Months";
    }

    const last6 = getRangeForPeriod("Last 6 Months");
    if (areDateRangesEqual(start, end, last6.start, last6.end)) {
      return "Last 6 Months";
    }

    const last12 = getRangeForPeriod("Last 12 Months");
    if (areDateRangesEqual(start, end, last12.start, last12.end)) {
      return "Last 12 Months";
    }

    return "Custom";
  };

  const getSafePickerRange = (range: {startDate: Date | string | null; endDate: Date | string | null; key: string;}) => {
      
    const parse = (date: Date | string | null, fallback: Date) =>
           date ? typeof date === "string" 
                    ? new Date(date + "T00:00:00")
                    : date
                : fallback;
  
    const today = new Date();
    const parsedStartDate = parse(range.startDate, today);
    const parsedEndDate = parse(range.endDate, parsedStartDate);

    return {
      ...range,
      startDate: parsedStartDate,
      endDate: parsedEndDate,
    };
  };

  const getTTBReport = async (page, size, filter: any, sortField: any, sortDirection: any) => {

    if (!isDateRangeValid(filter?.startDate, filter?.endDate)) {
      setDateRangeError("Date range cannot exceed 12 months.");
      return;
    }
    setDateRangeError(null);

    const controller = new AbortController();
    const signal = controller.signal;
    try {
      setIsLoading(true);
      setCompaniesList([]);
      // Cancel the previous request if any
      if (controllerRef.current) {
        controllerRef.current.abort();
      }

      // Store the current controller to cancel the request if needed
      controllerRef.current = controller;

      const response: any = await InventoryApiService.getTTBReport(page, size, filter, sortField, sortDirection, signal);
      if (response?.data?.content !== undefined) {
        setCompaniesList(response.data.content);
        setTotalCount(response.data.totalElements);
      } else if (typeof response?.data === "string") {
        setDateRangeError(response.data);
      }
      setIsLoading(false);
    } catch (error: any) {
      const msg = error?.response?.data;
      if (typeof msg === "string" && msg.toLowerCase().includes("date range")) {
        setDateRangeError(msg);
      } else {
        console.error("Error fetching data:", error);
      }
      setIsLoading(false);
    }
  }

  const exportTTBReport = async (filter: any, sortField: any, sortDirection: any) => {
    const controller = new AbortController();
    const signal = controller.signal;

    try {
      setIsLoading(true);

      // Cancel the previous request if any
      if (controllerRef.current) {
        controllerRef.current.abort();
      }

      // Store the current controller to cancel the request if needed
      controllerRef.current = controller;
      InventoryApiService.exportTTBReport(filter, sortField, sortDirection, signal);
      CommonService.Toast.fire({
        title: "Your export is in progress. We'll notify you when it's ready!",
        icon: "success",
      });
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setIsLoading(false);
    }
  };

  const tableDataWithIds = companiesList.map((rowData, index) => ({
    ...rowData,
  }));

  const handleSortChange = (sortModel) => {
    if (sortModel.length > 0) {
      const newSortField = sortModel[0].field;
      const newSortDirection = sortModel[0].sort; // "asc" or "desc" directly from DataGrid

      setSortField(newSortField);
      setSortDirection(newSortDirection);

      getTTBReport(page, pageSize, filter, newSortField, newSortDirection);
    }
  };

  const onPaginationModelChange = (newPaginationModel) => {
    setPage(newPaginationModel.page);
    setPageSize(newPaginationModel.pageSize);

    getTTBReport(newPaginationModel.page, newPaginationModel.pageSize, filter, sortField, sortDirection);
  };

  const getFixedPickerPosition = (triggerEl: HTMLDivElement | null, pickerWidth: number = 700, gap: number = 8) => {
    if (!triggerEl) return {};

    const rect = triggerEl.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    let left = rect.left;
    let top = rect.bottom + gap;

    if (left + pickerWidth > viewportWidth - 16) {
      left = viewportWidth - pickerWidth - 16;
    }

    if (left < 16) {
      left = 16;
    }

    return {
      position: "fixed" as const,
      top: `${top}px`,
      left: `${left}px`,
      zIndex: 9999,
    };
  };

  const handleMainPickerToggle = () => {
      if (!isMainDatePickerOpen) {
          setMainPickerStyle(getFixedPickerPosition(mainInputRef.current, 700));
      }
      setIsMainDatePickerOpen((prev) => !prev);
  };

  // main date picker change
  const handleTempChange = (start: Date | null, end: Date | null) => {
      setTempRange({
          startDate: start,
          endDate: end,
          key: "selection",
      });
  };
  
  // main date picker actions
  const handleMainClear = () => {
      const emptyRange = {
        startDate: null,
        endDate: null,
        key: "selection",
      };

      setTempRange(emptyRange);
      setSelectedDateRange(emptyRange);
      setSelectedPeriod("Custom");
      setDateRangeKey(prev => prev + 1);
      setIsMainDatePickerOpen(false);

      const tempFilter = {
          ...filter,
          startDate: null,
          endDate: null,
      };

      setFilter(tempFilter);
  };

  const handleClear = () => {
    handleMainClear();
    const tempFilter = {  
        startDate:null,
        endDate: null,
    };
    setSelectedPeriod("Custom");
    setDateRangeError(null);
    setFilter(tempFilter);
    getTTBReport(page, pageSize, tempFilter, sortField, sortDirection);
  }

  const handleMainCancel = () => {
    setTempRange(selectedDateRange);
    setIsMainDatePickerOpen(false);
  };

  const handleMainOk = () => {
    setSelectedDateRange(tempRange);
    setIsMainDatePickerOpen(false);

    const tempFilter = {
      ...filter,
      startDate: formatDate(tempRange.startDate),
      endDate: formatDate(tempRange.endDate),
    };

    setFilter(tempFilter);
    setSelectedPeriod(getMatchingPeriod(tempRange.startDate, tempRange.endDate));
  };

  const handleResetTimeRange = () => {
    setSelectedPeriod("Custom");
    const emptyRange = {
      startDate: null,
      endDate: null,
      key: "selection",
    };

    setTempRange(emptyRange);
    setSelectedDateRange(emptyRange);
    setDateRangeKey(prev => prev + 1);
    setIsMainDatePickerOpen(false);

    const tempFilter = {
        ...filter,
        startDate: null,
        endDate: null,
    };

    setFilter(tempFilter);
  };

  const handlePeriodChange = (selected: any) => {
    const value = selected.value;

    setSelectedPeriod(value);

    let start: Date, end: Date;
    const today = new Date();

    if (value === "Previous Month") {
      const range = CommonService.getPreviousMonthDateRange();
      start = range.startDate;
      end = range.endDate;
    } else if (value === "Last 3 Months") {
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      start = new Date(today.getFullYear(), today.getMonth() - 2, 1);
    } else if (value === "Last 6 Months") {
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      start = new Date(today.getFullYear(), today.getMonth() - 5, 1);
    } else if (value === "Last 12 Months") {
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      start = new Date(today.getFullYear() - 1, today.getMonth() + 1, 1);
    } else {
      start = null;
      end = null;
    }

    setSelectedDateRange({ startDate: start, endDate: end, key: "selection" });
    setTempRange({ startDate: start, endDate: end, key: "selection" });
    if (value === "Custom") {
      setDateRangeKey(prev => prev + 1);
    }

    const tempFilter = {
      ...filter,
      startDate: formatDate(start),
      endDate: formatDate(end),
    };

    setFilter(tempFilter);
  };

  const handleApply = () => {
    getTTBReport(page, pageSize, filter, sortField, sortDirection);
  };

  const columns: GridColDef[] = [
    {
      field: "month",
      headerName: "Month",
      flex: 1,
      minWidth: 200,
      sortable: true,
      valueGetter: (value: any, row: any) => `${row.month ?? ""}`,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
            {params.colDef.headerName}
          </Typography>
        </div>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip value={params.row.month} />
      ),
    },
    {
      field: "starting",
      headerName: "On Hand Starting",
      flex: 1,
      minWidth: 200,
      sortable: true,
      valueGetter: (value: any, row: any) => `${row.starting ?? ""}`,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
            {params.colDef.headerName}
          </Typography>
        </div>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip value={params.value || "-"} />
      ),
    },
    {
      field: "deposited",
      headerName: "Deposited",
      flex: 1,
      minWidth: 200,
      sortable: true,
      valueGetter: (value: any, row: any) => `${row.deposited ?? ""}`,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
            {params.colDef.headerName}
          </Typography>
        </div>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip value={params.value || "-"} />
      ),
    },
    {
      field: "transferred",
      headerName: "Transferred",
      flex: 1,
      minWidth: 200,
      sortable: true,
      valueGetter: (value: any, row: any) => `${row.transferred ?? ""}`,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
            {params.colDef.headerName}
          </Typography>
        </div>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip value={params.value || "-"} />
      ),
    },
    // {
    //   field: "loss",
    //   headerName: "Loss",
    //   flex: 1,
    //   minWidth: 200,
    //   sortable: true,
    //   valueGetter: (value: any, row: any) => `${row.loss ?? ""}`,
    //   headerClassName: "table-header border-0 my-2 h-100",
    //   renderHeader: (params) => (
    //     <div>
    //       <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
    //         {params.colDef.headerName}
    //       </Typography>
    //     </div>
    //   ),
    //   renderCell: (params: any) => (
    //     <CellWithTooltip value={params.value || "-"} />
    //   ),
    // },
    {
      field: "ending",
      headerName: "On Hand Ending",
      flex: 1,
      minWidth: 200,
      sortable: true,
      valueGetter: (value: any, row: any) => `${row.ending ?? ""}`,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
            {params.colDef.headerName}
          </Typography>
        </div>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip value={params.value || "-"} />
      ),
    },
    {
      field: "startingWithDeposits",
      headerName: "On Hand Starting + Deposited",
      flex: 1,
      minWidth: 200,
      sortable: true,
      valueGetter: (value: any, row: any) => `${row.startingWithDeposits ?? ""}`,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
            {params.colDef.headerName}
          </Typography>
        </div>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip value={params.value || "-"} />
      ),
    }
  ];

  return (<>
    <div className="reporting-filter-btn-wrapper ttb-rpt">
      {companiesList.length > 0 || isLoading ?
        <>
          <button title="Export to Excel"
            className="primary-btn d-inline-flex align-items-center py-1 export-btn"
            onClick={(e) => {
              e.currentTarget.blur(); // Removes focus after clicking
              exportTTBReport(filter, sortField, sortDirection);
            }}
          >
            <FileDownloadOutlinedIcon sx={{ marginTop: "5.5px" }} />
            Export
          </button>
        </>
      : null}
    </div>
    {dateRangeError && (
      <div className="alert barrel-alert alert-dismissible fade show mx-3 mt-2 mb-0" role="alert">
        <span className="d-flex align-items-center gap-2"><WarningIcon />{dateRangeError}</span>
        <button type="button" className="btn-close" aria-label="Close" onClick={() => setDateRangeError(null)}><CloseIcon /></button>
      </div>
    )}
    <div className="user-table-container report-module">
      <Accordion defaultActiveKey="0" onSelect={() => setIsMainDatePickerOpen(false)} className="filter-accordion">
        <Accordion.Item eventKey="0" className="filter-accordion-item">
          <Accordion.Header className="filter-accordion-header">
            <span className="accordion-filter-title">Filters</span>
            <button
              className="filter-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
            >
              Reset All
            </button>
          </Accordion.Header>
          <Accordion.Body className="filter-accordion-body p-0">
            <div className="filter-panel-accordion">

              <div className="filter-body">
                {/* Date range */}
                <div className="filter-section position-relative">
                  <div className="section-header">
                    <h4>Date range</h4>
                    <button className="section-btn" type="button" onClick={handleMainClear}>Reset</button>
                  </div>
                  {/* <label htmlFor="dateRange">Filter by Date Range</label> */}
                  <div className="date-input-wrapper" ref={mainInputRef}>
                    <input
                      type="text"
                      placeholder="Select date range"
                      readOnly
                      className="form-control"
                      value={
                        selectedDateRange.startDate && selectedDateRange.endDate
                          ? `${formatDate(selectedDateRange.startDate)} - ${formatDate(selectedDateRange.endDate)}`
                          : ""
                      }
                      onClick={handleMainPickerToggle}
                    />
                    <svg width="15" height="17" viewBox="0 0 15 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13.5 1.51172H12.7266V0H11.25V1.51172H3.72656V0H2.25V1.51172H1.47656C0.667969 1.51172 0 2.17969 0 3.02344V15.0117C0 15.8203 0.667969 16.5234 1.47656 16.5234H13.5C14.3086 16.5234 14.9766 15.8203 14.9766 15.0117V3.02344C14.9766 2.17969 14.3086 1.51172 13.5 1.51172ZM13.5 15.0117H1.47656V6.75H13.5V15.0117ZM13.5 5.27344H1.47656V3.02344H13.5V5.27344Z" fill="#9CA3AF" />
                    </svg>
                  </div>

                  {isMainDatePickerOpen && (
                    <div className="datepicker-menu" ref={datePickerRef} style={mainPickerStyle}>
                      <DateRange
                        key={dateRangeKey}
                        ranges={[getSafePickerRange(tempRange)]}
                        editableDateInputs={true}
                        dropdownMode="scroll"
                        onChange={(item: any) =>
                          handleTempChange(
                            item.selection.startDate ?? null,
                            item.selection.endDate ?? null
                          )
                        }
                        moveRangeOnFirstSelection={false}
                        months={2}
                        direction="horizontal"
                        showDateDisplay={false}
                      />
                      <div className="datepicker-menu-bottom">
                        <button type="button" onClick={handleMainCancel} className="datepicker-btn">
                          CANCEL
                        </button>
                        <button type="button" onClick={handleMainOk} className="datepicker-btn">
                          OK
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="filter-section">
                  <div className="section-header">
                    <h4>Time range</h4>
                    <button className="section-btn" type="button" onClick={handleResetTimeRange}>Reset</button>
                  </div>
                  {/* <label htmlFor="dateRange">Filter by Time Range</label> */}
                  <div className="mb-3">
                    <Select
                      value={options.find(opt => opt.value === selectedPeriod)}
                      onChange={handlePeriodChange}
                      options={options}
                      isSearchable={false}
                      classNamePrefix="react-select"
                      menuPortalTarget={document.body}   // render outside modal
                      menuPosition="fixed"              // avoid scroll issues
                      styles={{
                        menuPortal: base => ({ ...base, zIndex: 9999 }) // keep above modal
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="filter-bottom">
                <div className="filter-actions">
                  <button className="filter-btn" onClick={handleApply}><CheckIcon /> Apply</button>
                </div>
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
      <div className="userTable">
        <DataGrid
          rows={tableDataWithIds}
          columns={columns}
          className="data-grid"
          pagination
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={onPaginationModelChange}
          rowCount={totalCount}
          paginationMode="server"
          sortingMode="server"
          onSortModelChange={handleSortChange}
          disableRowSelectionOnClick={true}
          pageSizeOptions={[10, 20, 50, 100]}
          localeText={localeText}
          getRowId={(row) => `${row.month}`}
          loading={isLoading}
          slots={{
            loadingOverlay: DataGridLoader,
          }}
          disableColumnResize
          sx={{
            "& .MuiDataGrid-columnHeaderCheckbox": {
              borderBottom: "unset !important"
            },
          }}
        />
      </div>
    </div>

  </>
  );
}

export default TTBReport;